import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, Plus, Eye, EyeOff, Lock, Dog as DogIcon, Cat as CatIcon, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Uploaded videos live on the backend (/api/...); placeholder assets like
// /dog-sage.mp4 are served from the frontend origin.
const resolveMedia = (u) => (!u ? '' : u.startsWith('/api') ? `${BACKEND_URL}${u}` : u);

const AdminDogsPage = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  // Upload form state
  const [form, setForm] = useState({
    slug: '', name: '', species: 'dog', breed: '', vibe: '', ga_tag: '', order: 100,
  });
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const loadDogs = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dogs?password=${encodeURIComponent(pw)}`);
      setDogs(res.data.dogs || []);
      setAuthed(true);
      setPwError('');
    } catch (err) {
      if (err.response?.status === 401) {
        setPwError('Wrong password');
        setAuthed(false);
      } else {
        setPwError('Failed to load: ' + (err.message || 'unknown'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!password) return;
    loadDogs(password);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      toast.error('Pick a video file first');
      return;
    }
    if (!form.slug || !form.name) {
      toast.error('Slug + name required');
      return;
    }
    setUploading(true);
    setProgress(0);
    const uploadId = (crypto.randomUUID
      ? crypto.randomUUID()
      : `up-${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');
    const CHUNK = 5 * 1024 * 1024; // 5MB chunks bypass single-request/ingress limits
    const total = Math.max(1, Math.ceil(videoFile.size / CHUNK));
    try {
      for (let i = 0; i < total; i++) {
        const blob = videoFile.slice(i * CHUNK, (i + 1) * CHUNK);
        const cfd = new FormData();
        cfd.append('password', password);
        cfd.append('upload_id', uploadId);
        cfd.append('chunk_index', String(i));
        cfd.append('chunk', blob, 'chunk');
        await axios.post(`${API}/admin/dogs/upload-chunk`, cfd);
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const ffd = new FormData();
      ffd.append('password', password);
      ffd.append('upload_id', uploadId);
      Object.entries(form).forEach(([k, v]) => ffd.append(k, String(v)));
      if (thumbFile) ffd.append('thumbnail', thumbFile);
      const res = await axios.post(`${API}/admin/dogs/finalize`, ffd);
      setProgress(100);
      toast.success(`🐶 ${res.data.name} uploaded!`);
      setForm({ slug: '', name: '', species: 'dog', breed: '', vibe: '', ga_tag: '', order: 100 });
      setVideoFile(null);
      setThumbFile(null);
      setShowUpload(false);
      loadDogs(password);
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const toggleActive = async (slug, currentActive) => {
    try {
      const payload = JSON.stringify({ active: !currentActive });
      const fd = new FormData();
      fd.append('password', password);
      fd.append('payload', payload);
      await axios.patch(`${API}/admin/dogs/${slug}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${slug} → ${!currentActive ? 'active' : 'hidden'}`);
      loadDogs(password);
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const removeDog = async (slug) => {
    if (!window.confirm(`Permanently delete "${slug}" and its video? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/dogs/${slug}?password=${encodeURIComponent(password)}`);
      toast.success(`${slug} deleted`);
      loadDogs(password);
    } catch {
      toast.error('Delete failed');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-vent-secondary">
            <Lock className="w-4 h-4" />
            <h1 className="font-heading text-sm tracking-[0.25em] uppercase font-bold">Admin: Dogs</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-vent-secondary/50"
            autoFocus
            data-testid="admin-password-input"
          />
          {pwError && <p className="text-red-400 text-xs">{pwError}</p>}
          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-vent-secondary text-black font-heading font-bold text-sm tracking-wider flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'ENTER'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold">Dogs & Cats</h1>
            <p className="text-zinc-500 text-xs mt-1 tracking-wider uppercase">{dogs.length} total · {dogs.filter(d => d.active).length} active</p>
          </div>
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-vent-secondary text-black font-heading font-bold text-xs tracking-wider" data-testid="admin-add-btn">
            <Plus className="w-4 h-4" /> ADD
          </button>
        </div>

        {dogs.length === 0 && !showUpload && (
          <div className="text-center py-16 text-zinc-600">
            <DogIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No dogs uploaded yet. Click ADD to upload your first.</p>
          </div>
        )}

        {/* Upload modal */}
        {showUpload && (
          <form onSubmit={handleUpload} className="bg-zinc-900 border border-vent-secondary/20 rounded-2xl p-5 mb-6 space-y-3" data-testid="admin-upload-form">
            <h2 className="font-heading text-sm tracking-wider uppercase text-vent-secondary mb-2">Upload new buddy</h2>

            <div className="grid grid-cols-2 gap-2">
              <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="slug (e.g., george)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-slug" />
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Display name" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-name" />
              <select value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-species">
                <option value="dog">🐕 Dog</option>
                <option value="cat">🐈 Cat</option>
              </select>
              <input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} placeholder="Breed (optional)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-breed" />
              <input value={form.vibe} onChange={(e) => setForm({ ...form, vibe: e.target.value })} placeholder="Vibe (e.g., Calm soul)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm col-span-2" data-testid="upload-vibe" />
              <input value={form.ga_tag} onChange={(e) => setForm({ ...form, ga_tag: e.target.value })} placeholder="GA tag (optional)" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-ga" />
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 100 })} placeholder="Order" className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upload-order" />
            </div>

            <div>
              <label className="text-xs text-zinc-400 tracking-wider uppercase">Video (.mp4/.mov/.webm, max 300MB)</label>
              <input required type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(e) => setVideoFile(e.target.files[0])} className="w-full mt-1 text-sm text-zinc-300 file:bg-vent-secondary file:text-black file:font-bold file:px-3 file:py-1.5 file:rounded-md file:mr-3 file:border-0 file:text-xs file:tracking-wider file:uppercase" data-testid="upload-video" />
              {videoFile && <p className="text-[10px] text-vent-secondary mt-1">✓ {videoFile.name} · {(videoFile.size / (1024*1024)).toFixed(1)} MB</p>}
            </div>

            <div>
              <label className="text-xs text-zinc-400 tracking-wider uppercase">Thumbnail (optional, jpg/png)</label>
              <input type="file" accept="image/*" onChange={(e) => setThumbFile(e.target.files[0])} className="w-full mt-1 text-sm text-zinc-300 file:bg-zinc-700 file:text-white file:px-3 file:py-1.5 file:rounded-md file:mr-3 file:border-0 file:text-xs" data-testid="upload-thumb" />
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowUpload(false)} className="flex-1 py-2.5 rounded-lg bg-white/5 text-zinc-400 font-heading font-bold text-xs tracking-wider">CANCEL</button>
              <button type="submit" disabled={uploading} className="flex-1 py-2.5 rounded-lg bg-vent-secondary text-black font-heading font-bold text-xs tracking-wider flex items-center justify-center gap-2" data-testid="upload-submit">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> UPLOADING...</> : <><Upload className="w-3.5 h-3.5" /> UPLOAD</>}
              </button>
            </div>
          </form>
        )}

        {/* Dog list */}
        <div className="grid sm:grid-cols-2 gap-3">
          {dogs.map(d => (
            <div key={d.slug} className={`relative rounded-2xl overflow-hidden border ${d.active ? 'border-white/10' : 'border-red-500/20 opacity-60'} bg-zinc-900`} data-testid={`admin-dog-${d.slug}`}>
              <video
                src={resolveMedia(d.video_url)}
                poster={resolveMedia(d.thumbnail_url) || undefined}
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-44 object-cover bg-black"
                onError={(e) => { e.target.style.opacity = '0.25'; }}
                onMouseEnter={(e) => { const p = e.target.play(); if (p && p.catch) p.catch(() => {}); }}
                onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch { /* noop */ } }}
              />
              <div className="p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {d.species === 'cat' ? <CatIcon className="w-4 h-4 text-pink-400" /> : <DogIcon className="w-4 h-4 text-vent-secondary" />}
                    <span className="font-heading font-bold text-sm">{d.name}</span>
                  </div>
                  <span className="text-[10px] text-zinc-600 tracking-wider">#{d.order}</span>
                </div>
                <p className="text-xs text-zinc-500 truncate">{d.vibe || d.breed || '—'}</p>
                <p className="text-[10px] text-zinc-700 truncate">slug: {d.slug} · ga: {d.ga_tag}</p>
                <div className="flex gap-1.5 pt-2">
                  <button onClick={() => toggleActive(d.slug, d.active)} className="flex-1 text-[10px] py-1.5 rounded-md bg-white/5 hover:bg-white/10 tracking-wider uppercase flex items-center justify-center gap-1" data-testid={`admin-toggle-${d.slug}`}>
                    {d.active ? <><Eye className="w-3 h-3" /> Active</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                  </button>
                  <button onClick={() => removeDog(d.slug)} className="px-2 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400" data-testid={`admin-delete-${d.slug}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDogsPage;
